import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FormEvent, useState } from "react";
import { opsFetch } from "../lib/api";
import { Count, Empty, Field, Pill } from "../components/ui";

type Merchant = {
  slug: string;
  name: string;
  status: string;
  channel: string;
  commission_model: string;
  cpa_cny: number;
  cps_percent: number;
  contact: string;
  listing_count?: number;
};

const EMPTY = {
  slug: "",
  name: "",
  status: "active",
  channel: "local",
  commission_model: "cpa",
  cpa_cny: 0,
  cps_percent: 0,
  contact: "",
};

export function MerchantsPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<typeof EMPTY | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ["ops-merchants"],
    queryFn: () => opsFetch<{ data: { items: Merchant[] } }>("/api/ops/merchants"),
  });
  const save = useMutation({
    mutationFn: (body: typeof EMPTY) =>
      opsFetch(`/api/ops/merchants/${encodeURIComponent(body.slug.trim().toLowerCase())}`, {
        method: "PUT",
        body: JSON.stringify({ ...body, slug: body.slug.trim().toLowerCase() }),
      }),
    onSuccess: () => {
      setMsg("Saved");
      setEditing(null);
      void qc.invalidateQueries({ queryKey: ["ops-merchants"] });
    },
    onError: (e: Error) => setMsg(e.message),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!editing?.slug.trim() || !editing.name.trim()) return;
    save.mutate(editing);
  }

  return (
    <>
      {msg ? <p className="ops-ok">{msg}</p> : null}
      <div className="ops-toolbar">
        <button className="ops-btn ops-btn--primary" onClick={() => setEditing({ ...EMPTY })} type="button">
          New merchant
        </button>
        {q.data ? <Count label="merchants" n={q.data.data.items.length} /> : null}
      </div>
      {editing ? (
        <form className="ops-panel ops-form" onSubmit={onSubmit}>
          <h3 className="ops-panel__title">{editing.slug ? "Edit merchant" : "New merchant"}</h3>
          <div className="ops-form__grid">
            <Field label="Slug">
              <input className="ops-input" onChange={(e) => setEditing({ ...editing, slug: e.target.value })} required value={editing.slug} />
            </Field>
            <Field label="Name">
              <input className="ops-input" onChange={(e) => setEditing({ ...editing, name: e.target.value })} required value={editing.name} />
            </Field>
            <Field label="Channel">
              <select className="ops-input" onChange={(e) => setEditing({ ...editing, channel: e.target.value })} value={editing.channel}>
                <option value="ota">ota</option>
                <option value="local">local</option>
              </select>
            </Field>
            <Field label="Commission">
              <select className="ops-input" onChange={(e) => setEditing({ ...editing, commission_model: e.target.value })} value={editing.commission_model}>
                <option value="cpa">cpa</option>
                <option value="cps">cps</option>
              </select>
            </Field>
            <Field label="CPA (CNY per click)">
              <input className="ops-input" min={0} onChange={(e) => setEditing({ ...editing, cpa_cny: Number(e.target.value) })} type="number" value={editing.cpa_cny} />
            </Field>
            <Field label="CPS %">
              <input className="ops-input" min={0} onChange={(e) => setEditing({ ...editing, cps_percent: Number(e.target.value) })} type="number" value={editing.cps_percent} />
            </Field>
          </div>
          <div className="ops-toolbar">
            <button className="ops-btn ops-btn--primary" type="submit">Save</button>
            <button className="ops-btn ops-btn--ghost" onClick={() => setEditing(null)} type="button">Cancel</button>
          </div>
        </form>
      ) : null}
      {q.data && q.data.data.items.length > 0 ? (
        <div className="ops-table-wrap ops-panel">
          <table className="ops-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Status</th>
                <th>Listings</th>
                <th>Rate</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {q.data.data.items.map((m) => (
                <tr key={m.slug}>
                  <td>
                    {m.name}
                    <span className="ops-muted"> · {m.slug}</span>
                  </td>
                  <td><Pill value={m.status} /></td>
                  <td>{m.listing_count ?? 0}</td>
                  <td>
                    {m.cpa_cny === 0 && m.cps_percent === 0 ? (
                      <span className="ops-gap">Rate not set</span>
                    ) : m.commission_model === "cps" ? (
                      `${m.cps_percent}% CPS`
                    ) : (
                      `¥${m.cpa_cny} / click`
                    )}
                  </td>
                  <td>
                    <button className="ops-btn ops-btn--ghost" type="button" onClick={() => setEditing(m)}>
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {q.data && q.data.data.items.length === 0 ? <Empty>No merchants yet.</Empty> : null}
    </>
  );
}
